<template>
  <header class="row mb-1 md:mb-2 lg:mb-3 xl:mb-4 justify-between items-center">
    <div
      v-if="BrowserService.isDesktop()"
      class="col-12 mb-n3 pt-0 sm:pt-3"
    >
      <BreadCrumb :breadcrumb-list="breadcrumbList" />
    </div>
    <div class="col pt-3">
      <h1
        v-if="category"
        class="h2 mb-0"
      >
        {{ category.title }}
      </h1>
      <PlaceholderLoader
        v-else
        class="rounded-pill md:pb-1"
        style="max-width: 200px; min-height: 30px;"
      >
        <h1 class="pl-5 pr-5 mx-0 my-5" />
      </PlaceholderLoader>
      <p
        v-if="category"
        class="hidden sm:block"
        v-html="category.description"
      />
      <div
        v-if="category"
        class="sm:hidden block"
      >
        <TransitionCollapse v-show="readMore">
          <div class="text-sm mt-1">
            <span v-html="category.description" />
            <span
              class="underline"
              @click="readMore = !readMore"
            >
              {{ t('read_less') }}
            </span>
          </div>
        </TransitionCollapse>
        <div
          v-show="!readMore"
          class="text-sm mt-1"
        >
          <span v-html="shortenedAnnotation()" />...
          <span
            class="underline"
            @click="readMore = !readMore"
          >
            {{ t('read_more') }}
          </span>
        </div>
      </div>
      <PlaceholderLoader
        v-if="!category"
        class="mt-1 sm:mt-0 sm:pt-4 sm:pb-5 sm:mb-3 rounded"
        style="min-height: 42px;"
      />
    </div>
    <!-- favorite, structure type -->
    <div
      v-if="isSignedIn"
      class="flex col-auto pt-3 items-center lg:justify-end order-1 sm:order-0 fav-hero"
    >
      <!-- show favorite products -->
      <ProductsFavoritesFilter />
    </div>
  </header>
  <ProductsFilters />
</template>

<script setup>
import { striptags } from 'striptags'
import { onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useAppStore } from '@/stores/AppStore'
import { storeToRefs } from 'pinia'

import PlaceholderLoader from '@/components/general/BasePlaceholderLoader.vue'
import BreadCrumb from '@/components/general/BaseBreadcrumb.vue'
import TransitionCollapse from '@/components/general/BaseTransitionCollapse.vue'
import ProductsFavoritesFilter from '@/components/products/ProductsFavoritesFilter.vue'
import ProductsFilters from '@/components/products/ProductsFilters.vue'
import BrowserService from '@/services/browserService'

const appStore = useAppStore()
const { isSignedIn } = storeToRefs(appStore)

const props = defineProps({
  category: {
    type: Object,
    required: true,
  },
})

const { t } = useI18n()

const activeFilters = ref(false)
const breadcrumbList = ref([
  {
    name: t('ready_mixes'),
    link: '/products',
  },
])

const readMore = ref(false)

const initFilterButton = () => {
  const originalUrl = location.href
  if (originalUrl.includes('filter')) activeFilters.value = true
}

const shortenedAnnotation = () => {
  if (props.category && props.category.description && props.category.description.length > 64) {
    let shortAnnotation = striptags(props.category.description).slice(0, 64)
    // Cut last word in array
    shortAnnotation = shortAnnotation.slice(
      0,
      Math.max(0, shortAnnotation.lastIndexOf(' '))
    )
    return shortAnnotation;
  }
}

onMounted(() => {
  initFilterButton()
})
</script>

<i18n>
  {
    "cs": {
    "read_less": "číst méně",
    "read_more": "Číst více",
    "ready_mixes": "Hotovky"
},
    "sk": {
    "read_less": "čítať menej",
    "read_more": "Čítať ďalšie",
    "ready_mixes": "Hotovky"
},
    "pl": {
    "read_less": "zobacz mniej",
    "read_more": "Przeczytaj więcej",
    "ready_mixes": "Gotowe produkty"
},
    "hu": {
    "read_less": "kevesebbet mutat",
    "read_more": "Továbbolvasom",
    "ready_mixes": "Késztermékek"
},
    "de": {
    "read_less": "weniger lesen",
    "read_more": "Mehr lesen",
    "ready_mixes": "Fertige Mischung"
},
    "at": {
    "read_less": "weniger lesen",
    "read_more": "Mehr lesen",
    "ready_mixes": "Fertige Mischung"
},
    "ro": {
    "read_less": "mai puțin",
    "read_more": "mai mult",
    "ready_mixes": "Gata preparate"
},
    "fr": {
    "read_less": "lire moins",
    "read_more": "En savoir plus",
    "ready_mixes": "Produits prêts à déguster"
}
  }
</i18n>
